import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { PROXY } from '../../../components/constants';
import { reverseFormatTitle, formatTitleForURL } from '../../../components/functions';
import { Link } from "react-router-dom";
import ArrowRight from '../../../assets/arrow-long-right.svg';
import ArrowLeft from '../../../assets/arrow-long-left.svg';
import { EMAIL_ADD, PHONE, PHONE_NUMBER, OFFICE_HOURS} from '../../../components/constants'
import { ReactComponent as MailIcon } from '../../../assets/mail.svg';
import { ReactComponent as ClockIcon } from '../../../assets/clock.svg';
import SEO, { SITE_URL } from '../../../components/seo';

const getExcerpt = (value = '') => value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 155);

const getPublishedDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
};

export default function PostDetails() {
    const pathname = window.location.href;
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (slug) {
            const originalTitle = reverseFormatTitle(slug);
            fetchPosts(originalTitle);
        }
        fetchRecent();
    }, [slug]);

    const fetchPosts = (originalTitle) => {
        axios.get(`${PROXY}/fetch_posts_detail`, {
            params: { title: originalTitle }
        })
        .then(res => {
            setPost(res.data); 
            setLoading(false);
        })
        .catch(err => {
            console.error('Failed to fetch post:', err);
            setError('Failed to fetch post details');
            setLoading(false);
        });
    };

    const fetchRecent = () => {
        axios.get(`${PROXY}/fetch_posts`)
        .then(res => {
            const sortedPosts = res.data.sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateB - dateA;
            });
            setRecent(sortedPosts);
        })
        .catch(err => {
            console.error('Failed to fetch recent posts:', err);
        });
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div><SEO title="Post unavailable | Quilaton Law Office" description="The requested post is unavailable." robots="noindex,nofollow" />{error}</div>;
    if (!post || post.length === 0) return <div><SEO title="Post not found | Quilaton Law Office" description="The requested post was not found." robots="noindex,nofollow" />No post found</div>;

    const currentPost = post[0];
    const postPath = `/practices/posts/${formatTitleForURL(currentPost.title)}/`;
    const postDescription = getExcerpt(currentPost.details) || currentPost.title;
    const publishedDate = getPublishedDate(currentPost.date);
    const postImage = currentPost.image ? `${SITE_URL}/uploads/images/${currentPost.image}` : undefined;
    const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: currentPost.title,
        description: postDescription,
        mainEntityOfPage: `${SITE_URL}${postPath}`,
        ...(publishedDate ? { datePublished: publishedDate } : {}),
        ...(postImage ? { image: postImage } : {}),
        publisher: {
            '@type': 'Organization',
            name: 'Quilaton Law Office',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/qlo-logo.png` },
        },
    };

    // Extract the URLs and titles for the buttons
    const currentPostIndex = recent.findIndex(rec => rec.title === post[0]?.title);

    const previousPost = currentPostIndex > 0 ? recent[currentPostIndex - 1] : null;
    const nextPost = currentPostIndex < recent.length - 1 ? recent[currentPostIndex + 1] : null;

    return (
        <div className='mx-auto max-w-6xl bg-[#090909] px-5 py-12 text-zinc-300 lg:px-10'>
            <SEO
                title={`${currentPost.title} | Quilaton Law Office`}
                description={postDescription}
                path={postPath}
                image={postImage}
                schema={articleSchema}
                type="article"
            />
            {post[0]?.image && (
                <img alt={currentPost.title} src={`${PROXY}/uploads/images/${post[0].image}`} className="w-full rounded-2xl border border-zinc-700/80 object-cover shadow-2xl shadow-black/50" />
            )}
            <div className='mt-6 gap-8 lg:flex'>
                <div className="rounded-2xl border border-zinc-700/80 bg-gradient-to-br from-zinc-900 to-[#0c0c0c] p-7 shadow-xl shadow-black/20 lg:w-2/3 lg:p-9">
                    <div>
                        <h1 className="mt-1 font-fontMontserrat text-3xl leading-tight text-zinc-100">{post[0]?.title}</h1>
                        <small className="mt-4 block font-mono text-xs tracking-[0.1em] text-zinc-500">{post[0]?.date} | Quilaton Law Office</small>
                    </div>
                    <p className="mt-6 whitespace-pre-line leading-8 text-zinc-400">{post[0]?.details}</p>
                    <p className="mt-4 text-sm text-zinc-500">{post[0]?.tags}</p>
                    <div className='mt-5'>
                        <p>Share & Follow us on Facebook:</p>
                       <div className='flex flex-row gap-2'>
                            <a 
                                href={`https://www.facebook.com/sharer/sharer.php?u=${pathname}`} target="_blank" rel="noopener noreferrer"
                                className='rounded-xl border border-zinc-600 bg-zinc-800 px-4 py-2 text-sm text-zinc-100 transition hover:border-zinc-300 hover:bg-zinc-700'
                            >
                                Share on Facebook
                            </a>
                            <a 
                                href={`https://www.facebook.com/quilatonlawoffice`} target="_blank" rel="noopener noreferrer"
                                className='rounded-xl border border-zinc-600 bg-zinc-800 px-4 py-2 text-sm text-zinc-100 transition hover:border-zinc-300 hover:bg-zinc-700'>
                                Follow @quilatonlawoffice
                            </a>
                       </div>
                    </div>
                </div>
                <div className="mt-6 space-y-5 lg:mt-0 lg:w-1/3">
                    <div className="relative overflow-hidden rounded-2xl border border-zinc-700/80 bg-gradient-to-b from-zinc-900 to-black shadow-xl shadow-black/30">
                        <div className="relative space-y-4 p-6 text-zinc-300">
                            <h3 className="text-xl font-bold">Let us help you!</h3>
                                <p className="text-sm leading-6 text-zinc-400">
                                If you need any help, please feel free to contact us.
                                </p>

                                <p className="text-sm font-bold gap-1">
                                    <a href={`tel:${PHONE}`} className="text-sm">
                                        {PHONE_NUMBER}
                                    </a>
                                </p>

                                <div className="flex items-center space-x-2 text-sm">
                                    <MailIcon className="w-4 h-4" aria-hidden="true" />
                                    <a href={`mailto:${EMAIL_ADD}`} className="text-sm">
                                    {EMAIL_ADD}
                                    </a>
                                </div>

                                <div className="flex items-center space-x-2 text-sm">
                                    <ClockIcon className="w-4 h-4" aria-hidden="true" />
                                    <a href={`mailto:${EMAIL_ADD}`} className="text-sm">
                                    {OFFICE_HOURS}
                                    </a>
                                </div>
                        </div>
                    </div>
                    <div className='rounded-2xl border border-zinc-700/80 bg-zinc-900 p-6'>
                        <h2 className='font-fontMontserrat text-xl text-zinc-100'>Recent Posts</h2>
                        <div className='p-3'>
                            {recent.slice(0, 5).map((rec, index) => (
                                <div key={index} className='mt-4 border-b border-zinc-800 pb-4 last:border-0'>
                                    <Link
                                        to={`/practices/posts/${formatTitleForURL(rec.title)}/`}
                                        className='flex text-sm text-zinc-300 transition hover:text-white'
                                    >
                                        {rec.title}
                                    </Link>
                                    <p className='mt-1 font-mono text-xs text-zinc-500'>{rec.date}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className='mt-10 flex justify-between gap-6 border-t border-zinc-800 pt-6'>
                <button
                    onClick={() => window.location.href = `/practices/posts/${formatTitleForURL(previousPost?.title)}/`}
                    disabled={!previousPost}
                    className="max-w-[45%] text-left text-sm text-zinc-400 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <img src={ArrowLeft} alt='ArrowLeft' className='h-6'/>
                    {previousPost ? `${previousPost.title}` : 'No Previous Post'}
                </button>
                <button
                    onClick={() => window.location.href = `/practices/posts/${formatTitleForURL(nextPost?.title)}/`}
                    disabled={!nextPost}
                    className="flex max-w-[45%] flex-col items-end text-right text-sm text-zinc-400 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <img src={ArrowRight} alt='ArrowLeft' className='h-6'/>
                    {nextPost ? `${nextPost.title}` : 'No Next Post'}
                </button>
            </div>
        </div>
    );
}
